import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CheckerComments } from '../../core/types';
import { commentTimeString } from '../../core/utils';
import { CommentsModalComponent } from '../comments/comments-modal.component';
import { AUTH_CONTEXT, VALIDATION_SERVICE } from '../../core/interfaces';
import { ValidationLevel } from '../../core/types';
import { ControlsIconsModule } from '../icons.module';
import { HelpComponent } from '../help/help.component';

@Component({
    selector: 'wd-input-container',
    standalone: true,
    imports: [CommonModule, FormsModule, NgbTooltipModule, ControlsIconsModule, HelpComponent],
    templateUrl: 'input-container.component.html',
    styleUrls: ['../styles/input.component.css', '../styles/view.component.css'],
})
export class InputContainerComponent implements OnInit {
	@Input() name!: string;
	@Input() classes!: string;
	@Input() label?: string;
	@Input() helpText?: string;
	@Input() comments?: CheckerComments;
	@Input() hideComments = false;
	@Input() helpCurrentValue?: string;
	@Input() helpFilter?: string;
	@Output() helpValueSelected = new EventEmitter<{fbf: string, hbf: string}>();

	commentTimeString = commentTimeString;

	private modalService = inject(NgbModal);
	private aus = inject(AUTH_CONTEXT);
	private vs = inject(VALIDATION_SERVICE);

	constructor() { }

	ngOnInit(): void { }

	private isMyLastComment(idx: number) {
		return idx === this.comments!.comments!.length - 1
			&& this.comments!.comments[idx].name === this.aus.currentUser;
	}

	canComment(idx?: number) {
		if (!this.comments || this.comments.resolved || !this.aus.commenting) return false;
		return idx === undefined ? this.aus.commenting : this.isMyLastComment(idx);
	}

	canDeleteComment(idx: number) {
		return this.isMyLastComment(idx);
	}

	hasComments() {
		return !!this.comments;
	}

	commentsClass(prefix = ''): string {
		const rubber = this.aus.pen === 'rubber' ? 'rubber' : '';
		switch (this.comments?.type) {
			case ValidationLevel.danger: return `${rubber} ${prefix}danger`;
			case ValidationLevel.warning: return `${rubber} ${prefix}warning`;
			case ValidationLevel.ok: return `${rubber} ${prefix}message`;
			default: return '';
		}
	}

	onOpenCommentsClicked() {
		const modalRef = this.modalService.open(CommentsModalComponent);
		// Tiefe Kopie erstellen, damit Änderungen im Modal das Original nicht verändern
		const commentsCopy: CheckerComments = {
			...this.comments!,
			comments: this.comments!.comments.map(c => ({ ...c }))
		};
		modalRef.componentInstance.comments = commentsCopy;
		modalRef.componentInstance.name = this.name;
		modalRef.closed.subscribe((value?: CheckerComments | 'saved') => {
			// 'saved' = Modal hat bereits gespeichert, nichts mehr tun
			if (value === 'saved') {
				return;
			}
			// Wenn kein Wert zurückgegeben wurde (z.B. bei "Alle löschen"), clearComments aufrufen
			if (!value) {
				this.vs.clearComments(this.name);
				return;
			}
			// Prüfen ob der Kommentar bereits gelöscht wurde (durch deleteComment in comments-modal)
			// In diesem Fall existiert er nicht mehr in der Map und wir sollten nichts tun
			const existing = this.vs.hasComments(this.name);
			if (!existing) {
				// Kommentar wurde bereits gelöscht - nichts tun
				return;
			}
			// Kommentar existiert noch - aktualisieren
			this.vs.resetComments(this.name, value);
		})
	}

	onCommentsClicked() {
		if (this.aus.pen === 'rubber') this.onDeleteComments();
	}

	onDeleteComments() {
		delete this.comments;
		this.vs.clearComments(this.name);
	}

	onCommentFocusOut(idx: number) {
		if (this.comments?.comments[idx].msg === '' && this.canDeleteComment(idx))
			this.onDeleteComments();
	}
}
